/**
 * @file TokenRefreshManager.js
 * @description Manages token refresh based on expiration time and user activity with debouncing
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuthContext } from '../../store/AuthContext';
import { checkAndRefreshToken } from '../../utils/tokenRefresh';
import { parseJwt } from '../../utils/utils';
import { getText } from '../../i18n';

const TokenRefreshManager = () => {
    const { user, logout } = useAuthContext();
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const lastActivityTime = useRef(Date.now());
    const debounceTimerRef = useRef(null);

    const calculateTimeRemaining = () => {
        const token = localStorage.getItem('access_token');
        if (!token) return 0;

        try {
            const tokenPayload = parseJwt(token);
            const currentTime = Math.floor(Date.now() / 1000);
            return Math.max(0, tokenPayload.exp - currentTime);
        } catch (error) {
            console.error('Error parsing token:', error);
            return 0;
        }
    };

    const refreshToken = useCallback(() => {
        if (isRefreshing) return;

        const currentTimeRemaining = calculateTimeRemaining();
        if (currentTimeRemaining < 1800 && currentTimeRemaining > 0) {
            setIsRefreshing(true);
            const { first_name, last_name } = user;
            checkAndRefreshToken(true, first_name, last_name)
                .then(() => {
                    setTimeRemaining(calculateTimeRemaining());
                })
                .catch(error => {
                    console.error(
                        'Failed to refresh token on activity:',
                        error,
                    );
                })
                .finally(() => {
                    setIsRefreshing(false);
                });
        }
    }, [isRefreshing, user]);

    const handleUserActivity = useCallback(() => {
        lastActivityTime.current = Date.now();

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
            if (!isRefreshing) {
                refreshToken();
            }
        }, 300);
    }, [timeRemaining, isRefreshing, refreshToken]);

    useEffect(() => {
        const activityEvents = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
        ];

        const activityHandler = () => {
            handleUserActivity();
        };

        activityEvents.forEach(event => {
            window.addEventListener(event, activityHandler, { passive: true });
        });

        return () => {
            activityEvents.forEach(event => {
                window.removeEventListener(event, activityHandler);
            });

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [handleUserActivity]);

    useEffect(() => {
        const initialTimeRemaining = calculateTimeRemaining();
        setTimeRemaining(initialTimeRemaining);

        const timer = setInterval(() => {
            const remaining = calculateTimeRemaining();
            setTimeRemaining(remaining);

            if (remaining <= 0) {
                clearInterval(timer);
                localStorage.setItem('isAuthExpired', 'true');
                logout(true);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [logout]);

    const formatTime = seconds => {
        if (seconds === null) return '--:--:--';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return `${hours.toString().padStart(2, '0')}:${minutes
            .toString()
            .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div
            style={{
                position: 'fixed',
                bottom: 0,
                right: 0,
                fontSize: '10px',
                padding: '2px',
                zIndex: 9999,
                display: 'none',
            }}
            data-testid="token-timer"
        >
            {getText('msg_82ee4e4b4300')} {formatTime(timeRemaining)}
            {isRefreshing && ' (refreshing...)'}
        </div>
    );
};

export default TokenRefreshManager;
